import { useForm, useWatch } from 'react-hook-form'
import Image from 'next/image'
import { DevTool } from '@hookform/devtools'
import {
  ControlledRadioGroupInput,
  ControlledTextarea,
  ControlledToggle,
  ControlledRatingInput,
  Button,
  Title,
  ControlledNumberInput,
  ControlledDateInput,
  Badge,
  Card,
} from '@/components'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Tab } from '@headlessui/react'
import dayjs from 'dayjs'
import formatAddress from '@/lib/formatAddress'
import { AiOutlineCar } from 'react-icons/ai'
import { GiStreetLight, GiUndergroundCave } from 'react-icons/gi'
import { MdHighlightOff, MdGarage, MdApartment, MdLocalLaundryService } from 'react-icons/md'
import { RiParkingBoxLine } from 'react-icons/ri'
import clsx from 'clsx'
import { ImageUploader } from '../Images'
import { XCircleIcon } from '@heroicons/react/outline'
import { IoCalendarOutline, IoTodayOutline } from 'react-icons/io5'
import { BsCalendar4Week } from 'react-icons/bs'
import { superstructResolver } from '@hookform/resolvers/superstruct'
import {
  object,
  string,
  number,
  boolean,
  array,
  nullable,
  nonempty,
  size,
  date,
  trimmed,
  union,
  optional,
  coerce,
  unknown,
} from 'superstruct'

const schema = object({
  __typename: optional(string()),
  active: optional(boolean()),
  id: optional(string()),
  listingId: optional(string()),
  createdAt: optional(string()),
  userId: optional(string()),
  updatedAt: optional(string()),
  streetName: optional(nonempty(string())),
  streetNumber: optional(optional(nonempty(string()))),
  city: optional(nonempty(string())),
  neighbourhood: optional(nonempty(string())),
  region: optional(nonempty(string())),
  unitNumber: optional(string()),
  country: optional(nonempty(string())),
  postalZip: optional(nonempty(string())),
  listingType: optional(nonempty(string())),
  content: trimmed(size(string(), 35, 250000)),
  rating: size(number(), 1, 5),
  bedrooms: number(),
  bathrooms: number(),
  rentPrice: number(),
  rentFrequency: optional(nonempty(string())),
  shareTenancy: coerce(boolean(), string(), (value) => Boolean(value)),
  tenancyStart: union([date(), string()]),
  tenancyEnd: unknown(),
  stillTenant: coerce(boolean(), string(), (value) => Boolean(value)),
  petsWelcome: coerce(boolean(), string(), (value) => Boolean(value)),
  petsWelcomeStory: optional(trimmed(size(string(), 0, 250000))),
  noisyNeighbours: coerce(boolean(), string(), (value) => Boolean(value)),
  noisyNeighboursStory: optional(trimmed(size(string(), 0, 250000))),
  landlordResponsive: coerce(boolean(), string(), (value) => Boolean(value)),
  landlordResponsiveStory: optional(trimmed(size(string(), 0, 250000))),
  wellMaintained: coerce(boolean(), string(), (value) => Boolean(value)),
  wellMaintainedStory: optional(trimmed(size(string(), 0, 250000))),
  pests: coerce(boolean(), string(), (value) => Boolean(value)),
  pestsStory: optional(trimmed(size(string(), 0, 250000))),
  landlordLanguages: coerce(boolean(), string(), (value) => Boolean(value)),
  landlordLanguagesStory: optional(trimmed(size(string(), 0, 250000))),
  accessibility: coerce(boolean(), string(), (value) => Boolean(value)),
  accessibilityStory: optional(trimmed(size(string(), 0, 250000))),
  dishwasherAvailable: coerce(boolean(), string(), (value) => Boolean(value)),
  airConditioningIncluded: coerce(boolean(), string(), (value) => Boolean(value)),
  covidFriendly: coerce(boolean(), string(), (value) => Boolean(value)),
  parkingLocation: optional(nonempty(string())),
  parkingAvgPrice: nullable(number()),
  laundryLocation: optional(nonempty(string())),
  laundryIncluded: coerce(boolean(), string(), (value) => Boolean(value)),
  laundryAvgPrice: nullable(number()),
  dryerIncluded: coerce(boolean(), string(), (value) => Boolean(value)),
  dryerAvgPrice: nullable(number()),
  gasIncluded: coerce(boolean(), string(), (value) => Boolean(value)),
  gasAvgPrice: nullable(number()),
  hydroIncluded: coerce(boolean(), string(), (value) => Boolean(value)),
  hydroAvgPrice: nullable(number()),
  waterIncluded: coerce(boolean(), string(), (value) => Boolean(value)),
  waterAvgPrice: nullable(number()),
  images: optional(
    array(
      object({
        filePath: string(),
        fileName: string(),
        fileType: string(),
        width: number(),
        height: number(),
        fileId: string(),
      }),
    ),
  ),
})

interface AddressStepProps {
  handleUpdate: (data: any) => void
  handleBack?: () => void
  canGoBack?: boolean
  canproceed?: boolean
  data?: any
  listingData?: any[]
}

import DatesImage from '../../../../public/images/reviewicons/dates.png'
import AccessibilityImage from '../../../../public/images/reviewicons/accessibility.png'
import DetailsImage from '../../../../public/images/reviewicons/details.png'
import CostsImage from '../../../../public/images/reviewicons/costs.png'
import ExperienceImage from '../../../../public/images/reviewicons/experience.png'
import ImagesImage from '../../../../public/images/reviewicons/images.png'
import LandlordImage from '../../../../public/images/reviewicons/landlord.png'
import LaundryImage from '../../../../public/images/reviewicons/laundry.png'
import ParkingImage from '../../../../public/images/reviewicons/parking.png'
import PetsImage from '../../../../public/images/reviewicons/pets.png'
import UtilitiesImage from '../../../../public/images/reviewicons/utilities.png'
import acIcon from '../../../../public/icons/3d/ac.png'
import accessibilityIcon from '../../../../public/icons/3d/accessibility.png'
import dishwasherIcon from '../../../../public/icons/3d/dishwasher.png'
import gasIcon from '../../../../public/icons/3d/gas.png'
import hydroIcon from '../../../../public/icons/3d/hydro.png'
import languagesIcon from '../../../../public/icons/3d/languages.png'
import maintainIcon from '../../../../public/icons/3d/maintain.png'
import noisyIcon from '../../../../public/icons/3d/noisy.png'
import pestsIcon from '../../../../public/icons/3d/pests.png'
import petsIcon from '../../../../public/icons/3d/pets.png'
import responsiveIcon from '../../../../public/icons/3d/responsive.png'
import waterIcon from '../../../../public/icons/3d/water.png'

const TabList = [
  {
    title: 'Tenancy Dates',
    icon: DatesImage,
  },
  {
    title: 'Details',
    icon: DetailsImage,
  },
  {
    title: 'Rent Details',
    icon: CostsImage,
  },
  {
    title: 'Utilities',
    icon: UtilitiesImage,
  },

  {
    title: 'Laundry',
    icon: LaundryImage,
  },
  {
    title: 'Parking',
    icon: ParkingImage,
  },
  {
    title: 'Pets',
    icon: PetsImage,
  },
  {
    title: 'Accessibility',
    icon: AccessibilityImage,
  },
  {
    title: 'Landlord',
    icon: LandlordImage,
  },
  {
    title: 'Your experience',
    icon: ExperienceImage,
  },
  {
    title: 'Images',
    icon: ImagesImage,
  },
]

export const ReviewForm = ({ handleUpdate, data, listingData, canproceed = false }: AddressStepProps) => {
  const [images, setImages] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [completedIndex, setCompletedIndex] = useState(null)
  const el = useRef<HTMLDivElement>()
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<any>({
    mode: 'onBlur',
    resolver: superstructResolver(schema),
    defaultValues: {
      rentFrequency: 'monthly',
      rentPrice: 100,
      shareTenancy: true,
      ...data,
    },
  })

  useEffect(() => {
    if (data.images) {
      setImages(data.images)
    }
  }, [data])

  const handleImages = (uploadedImages: []) => {
    setImages(uploadedImages)
  }

  const handleSubmitFn = (data) => {
    const errorLoad = []

    if (!data.content) {
      errorLoad.push({ name: 'content', type: 'required', message: 'Review content' })
    }

    if (!data.bedrooms) {
      errorLoad.push({ name: 'bedrooms', type: 'required', message: 'Bedrooms' })
    }

    if (!data.bathrooms) {
      errorLoad.push({ name: 'bathrooms', type: 'required', message: 'Bedrooms' })
    }

    if (!data.rentFrequency) {
      errorLoad.push({ name: 'rentFrequency', type: 'required', message: 'Rent Frequency' })
    }

    if (!data.rating) {
      errorLoad.push({ name: 'rating', type: 'required', message: 'Rating' })
    }

    if (!data.rentPrice) {
      errorLoad.push({ name: 'rentPrice', type: 'required', message: 'Rent price' })
    }

    if (!data.tenancyStart) {
      errorLoad.push({ name: 'tenancyStart', type: 'required', message: 'Tenancy start' })
    }

    if (!data.tenancyEnd && !data.stillTenant) {
      errorLoad.push({ name: 'tenancyEnd', type: 'required', message: 'Tenancy end' })
    }

    if (errorLoad.length) {
      errorLoad.forEach(({ name, type, message }) => setError(name, { type, message }))
    } else {
      handleUpdate({ ...data, images })
    }
  }

  const handleChange = (val) => {
    if (completedIndex === null) {
      setCompletedIndex(0)
    } else if (completedIndex < val) {
      setCompletedIndex(val - 1)
    }
    setSelectedIndex(val)

    if (el.current) {
      el.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  const watchTenancyStart = useWatch({ control, name: 'tenancyStart' })
  const watchTenancyEnd = useWatch({ control, name: 'tenancyEnd' })
  const watchStillTenancy = useWatch({ control, name: 'stillTenant' })
  const gasIncluded = useWatch({
    control,
    name: 'gasIncluded',
  })
  const hydroIncluded = useWatch({
    control,
    name: 'hydroIncluded',
  })
  const waterIncluded = useWatch({
    control,
    name: 'waterIncluded',
  })
  const parkingLocation = useWatch({
    control,
    name: 'parkingLocation',
  })
  const laundryLocation = useWatch({
    control,
    name: 'laundryLocation',
  })
  const laundryIncluded = useWatch({
    control,
    name: 'laundryIncluded',
  })
  const dryerIncluded = useWatch({
    control,
    name: 'dryerIncluded',
  })

  return (
    <>
      <DevTool control={control} />
      <header className="pt-4 sm:pt-5 pb-4 lg:pb-16 max-w-7xl mx-auto px-4" ref={el}>
        <Title as="p" size="xl" className="mt-4 !text-lg lg:!text-4xl">
          {formatAddress(listingData || data)}
        </Title>
      </header>

      <div className="lg:grid gap-12 grid-cols-10 px-4">
        <Tab.Group selectedIndex={selectedIndex} onChange={handleChange} vertical as={Fragment} manual>
          <div className=" col-span-2  z-30 hidden lg:block">
            <Tab.List className="space-y-2 sticky top-24">
              {TabList.map((tabInfo, idx) => (
                <Tab as={Fragment} key={tabInfo.title}>
                  {({ selected }) => (
                    <button
                      type="button"
                      className={clsx(
                        !canproceed && completedIndex < idx ? 'opacity-50 pointer-events-none' : '',
                        selected
                          ? 'bg-gradient-to-tr from-brand to-brand-400 border-transparent text-white shadow-lg shadow-brand/30 dark:shadow-brand/20 !opacity-100'
                          : 'dark:text-zinc-400 hover:dark:text-zinc-50 text-zinc-600 hover:text-brand-600 hover:bg-zinc-200 hover:bg-opacity-50 hover:dark:bg-zinc-800 border-transparent hover:border-brand bg-zinc-200/60 dark:bg-zinc-900 shadow-sm ',
                        'group w-full px-6 py-3 flex justify-start items-center text-sm font-bold  relative focus:outline-brand flex-1 rounded-md',
                      )}
                    >
                      <figure className={clsx('w-8 object-cover lg:mr-4 drop-shadow-xl')}>
                        <Image src={tabInfo.icon} />
                      </figure>
                      <span className="sr-only lg:not-sr-only">{tabInfo.title}</span>
                    </button>
                  )}
                </Tab>
              ))}
            </Tab.List>
          </div>
          <form onSubmit={handleSubmit(handleSubmitFn)} className="w-full flex-1 px-0 sm:pb-6 col-span-8">
            <Tab.Panels>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-10">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    When did you live there?
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-12  py-4">
                      <div className="flex-1">
                        <ControlledDateInput
                          label="Tenancy Start Date"
                          name="tenancyStart"
                          rules={{ valueAsDate: true }}
                          control={control}
                          maxDate={dayjs(watchTenancyEnd).subtract(1, 'day').toDate()}
                        />
                      </div>
                      <div className="flex-1 space-y-4">
                        <ControlledDateInput
                          label="Tenancy End Date"
                          name="tenancyEnd"
                          control={control}
                          rules={{ valueAsDate: true }}
                          disabled={watchStillTenancy}
                          minDate={dayjs(watchTenancyStart).add(1, 'day').toDate()}
                        />

                        <ControlledToggle
                          control={control}
                          name="stillTenant"
                          label="Are you still a tenant of this rental?"
                          reverse
                          large
                        />
                      </div>
                      <div className="mb-8">
                        <ControlledToggle
                          control={control}
                          name="shareTenancy"
                          label="Would you like us to hide the tenancy dates?"
                          description="This helps keep your testimonial anonymous."
                          reverse
                          large
                        />
                      </div>
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      forward
                      alt
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    Cool. What's the place like?
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-12  py-4">
                      <ControlledRadioGroupInput
                        control={control}
                        name="bedrooms"
                        label="Number of Bedrooms"
                        rules={{ required: true }}
                        options={[
                          { name: 0, label: 'Bachelor', description: '' },
                          { name: 1, label: '1', description: '' },
                          { name: 2, label: '2', description: '' },
                          { name: 3, label: '3', description: '' },
                          { name: '3+', label: '3+', description: '' },
                        ]}
                      />

                      <ControlledRadioGroupInput
                        control={control}
                        name="bathrooms"
                        label="Number of Bathrooms"
                        rules={{ required: true }}
                        options={[
                          { name: 0, label: '0', description: '' },
                          { name: 1, label: '1', description: '' },
                          { name: 2, label: '2', description: '' },
                          { name: 3, label: '3', description: '' },
                          { name: '3+', label: '3+', description: '' },
                        ]}
                      />

                      <div className="space-x-6 flex items-center">
                        <div className="rounded-full p-2.5 bg-green-200  shadow-lg shadow-green-500/50 h-16 w-16 relative">
                          <div className="absolute left-1 -top-2 z-10 h-16 w-16">
                            <Image src={dishwasherIcon} />
                          </div>
                        </div>
                        <ControlledToggle
                          control={control}
                          name="dishwasherAvailable"
                          label="Is a dishwasher available in the unit?"
                          stacked
                        />
                      </div>
                      <div className="  space-x-6 flex items-center">
                        <div className="rounded-full p-2.5 bg-amber-200  shadow-lg shadow-amber-500/50 h-16 w-16 relative">
                          <div className="absolute left-2 top-2 z-10 h-12 w-12">
                            <Image src={acIcon} />
                          </div>
                        </div>
                        <ControlledToggle
                          control={control}
                          name="airConditioningIncluded"
                          label="Does the unit have air conditioning?"
                          stacked
                        />
                      </div>
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    What did you pay for rent?
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-12  py-4">
                      <ControlledRadioGroupInput
                        control={control}
                        name="rentFrequency"
                        rules={{ required: true }}
                        label="How often did you pay rent?"
                        options={[
                          {
                            name: 'monthly',
                            label: 'Monthly',
                            description: '',
                            icon: <IoCalendarOutline className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'weekly',
                            label: 'Weekly',
                            description: '',
                            icon: <BsCalendar4Week className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'daily',
                            label: 'Daily',
                            description: '',
                            icon: <IoTodayOutline className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                        ]}
                      />

                      <div className="w-full sm:w-64">
                        <ControlledNumberInput
                          label="Monthly Rent Price"
                          control={control}
                          name="rentPrice"
                          min={100}
                          max={10000}
                          rules={{ required: true }}
                          required
                          toFixedValue={2}
                          displayFormat="$"
                          displayPrice
                          defaultValue={100}
                        />
                      </div>
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    How about the price of utilities?
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-16  py-4">
                      <div className="flex flex-col sm:flex-row flex-wrap items-start">
                        <div className="flex items-center gap-6 ">
                          <div className="rounded-full p-2.5 bg-lime-100  shadow-lg shadow-lime-500/50 h-16 w-16 relative">
                            <div className="absolute left-1 -top-2 z-10 h-16 w-16 -rotate-12">
                              <Image src={gasIcon} />
                            </div>
                          </div>

                          <ControlledToggle
                            className="w-64"
                            control={control}
                            name="gasIncluded"
                            label="Is gas included?"
                            stacked
                          />
                        </div>

                        <ControlledNumberInput
                          label="Average monthly gas bill"
                          control={control}
                          name="gasAvgPrice"
                          min={100}
                          max={10000}
                          toFixedValue={2}
                          displayFormat="$"
                          displayPrice
                          defaultValue={50}
                          className="w-full sm:w-64"
                          disabled={gasIncluded}
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row flex-wrap items-start">
                        <div className="flex items-center gap-6">
                          <div className="rounded-full p-2.5 bg-red-100  shadow-lg shadow-red-400/50 h-16 w-16 relative">
                            <div className="absolute left-1 -top-2 z-10 h-16 w-16 ">
                              <Image src={hydroIcon} />
                            </div>
                          </div>
                          <ControlledToggle
                            className="w-full sm:w-64"
                            control={control}
                            name="hydroIncluded"
                            label="Electricity included?"
                            stacked
                          />
                        </div>
                        <ControlledNumberInput
                          label="Average monthly Electricity bill"
                          control={control}
                          name="hydroAvgPrice"
                          min={100}
                          max={10000}
                          toFixedValue={2}
                          displayFormat="$"
                          displayPrice
                          defaultValue={50}
                          className="w-full sm:w-64"
                          disabled={hydroIncluded}
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row flex-wrap items-start">
                        <div className="flex items-center gap-6">
                          <div className="rounded-full p-2.5 bg-cyan-100  shadow-lg shadow-cyan-400/50 h-16 w-16 relative">
                            <div className="absolute left-1 -top-2 z-10 h-16 w-16 ">
                              <Image src={waterIcon} />
                            </div>
                          </div>
                          <ControlledToggle
                            className="w-full sm:w-64"
                            control={control}
                            name="waterIncluded"
                            label="Water included?"
                            stacked
                          />
                        </div>
                        <ControlledNumberInput
                          label="Average monthly water bill"
                          control={control}
                          name="waterAvgPrice"
                          min={100}
                          max={10000}
                          toFixedValue={2}
                          displayFormat="$"
                          displayPrice
                          defaultValue={50}
                          className="w-full sm:w-64"
                          disabled={waterIncluded}
                        />
                      </div>
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    OK. Real talk. Laundry?
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-16  py-4">
                      <ControlledRadioGroupInput
                        control={control}
                        name="laundryLocation"
                        label="Laundry location"
                        options={[
                          {
                            name: 'none',
                            label: 'None',
                            description: '',
                            icon: <MdHighlightOff className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'in-unit',
                            label: 'In unit',
                            description: '',
                            icon: <MdApartment className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'basement',
                            label: 'Basement',
                            description: '',
                            icon: <GiUndergroundCave className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'laundromat',
                            label: 'Laundromat',
                            description: '',
                            icon: <MdLocalLaundryService className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                        ]}
                      />
                      <div
                        className={clsx(
                          'space-y-6 sm:space-y-0  sm:items-start sm:space-x-6 ',
                          laundryLocation === 'none' ? 'hidden' : 'sm:flex',
                        )}
                      >
                        <ControlledToggle
                          className="w-full sm:w-72"
                          control={control}
                          name="laundryIncluded"
                          label="Is the washing machine free?"
                          large
                          reverse
                        />

                        <ControlledNumberInput
                          label="Average washing machine price per load"
                          control={control}
                          name="laundryAvgPrice"
                          min={100}
                          max={10000}
                          toFixedValue={2}
                          displayFormat="$"
                          displayPrice
                          defaultValue={0.25}
                          className="w-full sm:w-72"
                          disabled={laundryIncluded}
                        />
                      </div>
                      <div
                        className={clsx(
                          'space-y-6 sm:space-y-0 sm:items-start sm:space-x-6 ',
                          laundryLocation === 'none' ? 'hidden' : 'sm:flex',
                        )}
                      >
                        <ControlledToggle
                          className="w-full sm:w-64"
                          control={control}
                          name="dryerIncluded"
                          label="Is the dryer free?"
                          large
                          reverse
                        />

                        <ControlledNumberInput
                          label="Average dryer price per load"
                          control={control}
                          name="dryerAvgPrice"
                          min={100}
                          max={10000}
                          toFixedValue={2}
                          displayFormat="$"
                          displayPrice
                          defaultValue={0.25}
                          className="w-full sm:w-72"
                          disabled={dryerIncluded}
                        />
                      </div>
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    How 'bout parking?
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-8  py-4">
                      <ControlledRadioGroupInput
                        control={control}
                        name="parkingLocation"
                        label="Parking location"
                        options={[
                          {
                            name: 'none',
                            label: 'None',
                            description: '',
                            icon: <MdHighlightOff className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'garage',
                            label: 'Garage',
                            description: '',
                            icon: <MdGarage className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'driveway',
                            label: 'Driveway',
                            description: '',
                            icon: <AiOutlineCar className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'lot',
                            label: 'Lot',
                            description: '',
                            icon: <RiParkingBoxLine className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'street',
                            label: 'Street',
                            description: '',
                            icon: <GiStreetLight className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                          {
                            name: 'underground',
                            label: 'Underground',
                            description: '',
                            icon: <GiUndergroundCave className="w-8 h-8 fill-inherit stroke-inherit" />,
                          },
                        ]}
                      />
                      <div className={clsx(parkingLocation === 'none' ? 'hidden' : 'sm:block')}>
                        <ControlledNumberInput
                          label="Parking Price"
                          control={control}
                          name="parkingAvgPrice"
                          min={100}
                          max={10000}
                          toFixedValue={2}
                          displayFormat="$"
                          displayPrice
                          defaultValue={10}
                          className="w-full sm:w-72"
                        />
                      </div>
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    Were fuzzy friends welcome?
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-16 py-4 sm:grid sm:space-y-0 sm:grid-cols-6 gap-2 ">
                      <div className="col-span-2 flex space-x-4 items-start">
                        <div className="rounded-full p-2.5 bg-yellow-100  shadow-lg shadow-yellow-400/50 h-16 w-16 relative">
                          <div className="absolute left-2 top-2 z-10 h-12 w-12 ">
                            <Image src={petsIcon} />
                          </div>
                        </div>

                        <ControlledToggle
                          control={control}
                          name="petsWelcome"
                          label="Pets were allowed"
                          stacked
                          large
                        />
                      </div>

                      <ControlledTextarea
                        name="petsWelcomeStory"
                        control={control}
                        label="Your experience with your pets in the rental"
                        className="col-span-4"
                        // description="Cats and Landlords don't always get along."
                      />
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    Accessibility experiences.
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-16 py-4 sm:grid sm:space-y-0 sm:grid-cols-6 gap-2 ">
                      <div className="col-span-2 flex space-x-4 items-start">
                        <div className="rounded-full p-2.5 bg-lime-100  shadow-lg shadow-lime-400/50 h-16 w-16 relative">
                          <div className="relative left-0 top-0 z-10 h-12 w-12 ">
                            <Image src={accessibilityIcon} />
                          </div>
                        </div>
                        <ControlledToggle
                          control={control}
                          name="accessibility"
                          label="Is the unit and building accessibility friendly?"
                          stacked
                          large
                        />
                      </div>
                      <ControlledTextarea
                        name="accessibilityStory"
                        control={control}
                        label="Share some details on the elevators, stairs and more."
                        description="Come as you are."
                        className="col-span-4"
                      />
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    Let's talk about the landlord.
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-16 py-4 ">
                      <div className="sm:grid sm:space-y-0 sm:grid-cols-6 gap-2   ">
                        <div className="col-span-2 flex space-x-4 items-start">
                          <div className="rounded-full p-2.5 bg-cyan-100 shadow-lg shadow-cyan-400/50 h-16 w-16 relative">
                            <div className="relative -left-1 -top-4 z-10 h-16 w-16 ">
                              <Image src={responsiveIcon} />
                            </div>
                          </div>

                          <ControlledToggle
                            control={control}
                            name="landlordResponsive"
                            label="Is the landlord responsive to requests?"
                            stacked
                          />
                        </div>

                        <ControlledTextarea
                          name="landlordResponsiveStory"
                          control={control}
                          label="Your experience with requests to the landlord"
                          description="A survey shows that people get used to the sounds of their neighbours rather quickly. Did you?"
                          className="col-span-4"
                        />
                      </div>

                      <div className="sm:grid space-y-8 sm:space-y-0 sm:grid-cols-6 gap-8 ">
                        <div className="col-span-2 flex space-x-4 items-start">
                          <div className="rounded-full p-2.5 bg-amber-100 shadow-lg shadow-amber-400/50 h-16 w-16 relative">
                            <div className="relative -left-1 -top-4 z-10 h-16 w-16 ">
                              <Image src={maintainIcon} />
                            </div>
                          </div>

                          <ControlledToggle
                            control={control}
                            name="wellMaintained"
                            label="Is the property well maintained?"
                            stacked
                          />
                        </div>

                        <ControlledTextarea
                          name="wellMaintainedStory"
                          control={control}
                          label="Let us know if the property management team kept things tidy."
                          description="Hint: we assume it was, we just want to know if the building is still standing."
                          className="col-span-4"
                        />
                      </div>

                      <div className="sm:grid space-y-8 sm:space-y-0 sm:grid-cols-6 gap-8 py-8  ">
                        <div className="col-span-2 flex space-x-4 items-start">
                          <div className="rounded-full p-2.5 bg-rose-100 shadow-lg shadow-rose-400/50 h-16 w-16 relative">
                            <div className="relative left-0 top-0 z-10 h-12 w-12 ">
                              <Image src={languagesIcon} />
                            </div>
                          </div>

                          <ControlledToggle
                            control={control}
                            name="landlordLanguages"
                            label="Did the landlord speak any languages other than English?"
                            description='They just spoke "landlord."'
                            className="col-span-2"
                            stacked
                          />
                        </div>

                        <ControlledTextarea
                          name="landlordLanguagesStory"
                          control={control}
                          label="Tell us what languages you're certain they spoke"
                          className="col-span-4"
                        />
                      </div>
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>

              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    Spill the tea.
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="space-y-6 py-4">
                      <div className="">
                        <ControlledRatingInput
                          control={control}
                          label="Your overall rating"
                          name="rating"
                          description="Like, did you love it?"
                        />
                      </div>
                      <div className="pt-6 pb-16">
                        <ControlledTextarea
                          name="content"
                          control={control}
                          rules={{ required: true }}
                          label="Your Review"
                        />
                      </div>

                      <div className="pb-6">
                        <ControlledToggle
                          control={control}
                          name="covidFriendly"
                          label="Is the building COVID friendly?"
                        />
                      </div>

                      <div className="sm:grid space-y-8 sm:space-y-0 sm:grid-cols-6 gap-8 py-8 ">
                        <div className="col-span-2 flex space-x-4 items-start">
                          <div className="rounded-full p-2.5 bg-emerald-100 shadow-lg shadow-emerald-400/50 h-16 w-16 relative">
                            <div className="relative -left-1 -top-4 z-10 h-16 w-16 ">
                              <Image src={noisyIcon} />
                            </div>
                          </div>

                          <ControlledToggle
                            control={control}
                            name="noisyNeighbours"
                            label="Are the neighbours noisy?"
                            stacked
                          />
                        </div>
                        <ControlledTextarea
                          name="noisyNeighboursStory"
                          control={control}
                          label="Your experience with the neighbours"
                          description="A survey shows that people get used to the sounds of their neighbours rather quickly. Did you?"
                          className="col-span-4"
                        />
                      </div>

                      <div className="sm:grid space-y-8 sm:space-y-0 sm:grid-cols-6 gap-8 py-8 ">
                        <div className="col-span-2 flex space-x-4 items-start">
                          <div className="rounded-full p-2.5 bg-teal-100 shadow-lg shadow-teal-400/50 h-16 w-16 relative">
                            <div className="relative left-0 -top-1 z-10 h-12 w-12 ">
                              <Image src={pestsIcon} />
                            </div>
                          </div>
                          <ControlledToggle control={control} name="pests" label="Are there any pests?" stacked large />
                        </div>

                        <ControlledTextarea
                          name="pestsStory"
                          control={control}
                          label="Did you find any insects in your unit? Spiders, mosquitos, flies, anything? "
                          className="col-span-4"
                          description="I literally can't handle them. Yuck."
                        />
                      </div>
                    </div>
                  </Card>
                  <div className="flex justify-end mt-10 space-x-4">
                    <Button
                      small
                      back
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) - 1)
                      }}
                    >
                      Back.
                    </Button>
                    <Button
                      alt
                      forward
                      type="button"
                      onClick={() => {
                        handleChange(Number(selectedIndex) + 1)
                      }}
                    >
                      Next.
                    </Button>
                  </div>
                </div>
              </Tab.Panel>
              <Tab.Panel className="pt-3 focus:outline-none focus:ring-0">
                <header className="mb-12">
                  <Badge large>Step {selectedIndex + 2}</Badge>
                  <Title as="h1" size="2xl" className="mt-4">
                    Pictures tell the best story.
                  </Title>
                </header>
                <div className="w-full">
                  <Card>
                    <div className="py-4">
                      <ImageUploader handleUpdate={handleImages} data={images} />
                    </div>
                  </Card>
                </div>
              </Tab.Panel>

              <div className="space-x-4 flex items-center justify-end pt-6 mt-6 sm:mt-10">
                {Object.keys(errors).length > 0 ? (
                  <div className="max-w-lg flex items-start space-x-2 relative">
                    <XCircleIcon className="h-6 w-6 text-rose-500 sm:absolute -left-6 top-0" aria-hidden="true" />
                    <p className="text-sm text-rose-500">
                      {Object.values(errors)
                        .map(({ message }) => message)
                        .join(', ')}{' '}
                      are missing
                    </p>
                  </div>
                ) : null}
                {selectedIndex === 10 ? (
                  <Button type="submit" alt>
                    Done.
                  </Button>
                ) : null}
              </div>
            </Tab.Panels>
          </form>
        </Tab.Group>
      </div>
    </>
  )
}
